import fallbackVariables from './variables.json';
import { REPORT_TYPE_IDS } from './reportTypes';

let variables = {};
let alternativeMap = {};
let trabecularVariables = { xct1: [], xct2: [] };
let corticalVariables = { xct1: [], xct2: [] };
let bothVariables = { xct1: [], xct2: [] };

const createReportRequirements = () => REPORT_TYPE_IDS.reduce((requirements, reportType) => {
    requirements[reportType] = {
        optional: { demographics: [], scan: [] },
        required: { demographics: [], scan: [] }
    };
    return requirements;
}, {});

let reportRequirements = createReportRequirements();
let initializationPromise = null;
let isInitialized = false;
const superscripts = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '-': '⁻',
};

const hydrateVariables = (sourceVariables) => JSON.parse(JSON.stringify(sourceVariables || {}));

const resetDerivedState = () => {
    alternativeMap = {};
    trabecularVariables = { xct1: [], xct2: [] };
    corticalVariables = { xct1: [], xct2: [] };
    bothVariables = { xct1: [], xct2: [] };
    reportRequirements = createReportRequirements();
};

const normalizeUnit = (unit) => {
    if (!unit || unit === '-') {
        return '';
    }

    if (!isNaN(unit.slice(-1))) {
        return unit.split('').map((char) => superscripts[char] || char).join('');
    }

    return unit;
};

// Create a hash map of alternatives to variables so searching will be O(1)
// in the same loop, populate store trabecular and cortical variables.
const buildDerivedStructures = (sourceVariables) => {
    variables = hydrateVariables(sourceVariables);
    resetDerivedState();

    for (let key in variables) {
    // format units
        variables[key].Units = normalizeUnit(variables[key].Units);
        const variable = variables[key];
        let needed = false;
        const group = variable.Group ? variable.Group.toLowerCase() === 'demographics' ? 'demographics' : 'scan' : 'scan';

        if (variable.Optional) {
            for (let optional of variable.Optional.toLowerCase().split(',')) {
                optional = optional.trim();
                reportRequirements[optional] = reportRequirements[optional] || { optional: {}, required: {} };
                if (group === 'scan') {
                    if (variables[key]["Has Normative?"] === 'yes') {
                        reportRequirements[optional]['optional'][group] ? reportRequirements[optional]['optional'][group].push(key) : reportRequirements[optional]['optional'][group] = [key];
                    }
                } else {
                    reportRequirements[optional]['optional'][group] ? reportRequirements[optional]['optional'][group].push(key) : reportRequirements[optional]['optional'][group] = [key];
                }
            }
            needed = true;
        }

        if (variable.Required) {
            for (let required of variable.Required.toLowerCase().split(',')) {
                required = required.trim();
                reportRequirements[required] = reportRequirements[required] || { optional: {}, required: {} };
                if (group === 'scan') {
                    if (variables[key]["Has Normative?"] === 'yes') {
                        reportRequirements[required]['required'][group] ? reportRequirements[required]['required'][group].push(key) : reportRequirements[required]['required'][group] = [key];
                    }
                } else {
                    reportRequirements[required]['required'][group] ? reportRequirements[required]['required'][group].push(key) : reportRequirements[required]['required'][group] = [key];
                }
            }
            needed = true;
        }

        if (variable.Tissue === 'trabecular' && needed) {
            if (reportRequirements.xct1.optional.scan.indexOf(key) > -1) {
                trabecularVariables.xct1.push(variable.Abbreviation.toLowerCase());
            }
            if (reportRequirements.xct2.optional.scan.indexOf(key) > -1) {
                trabecularVariables.xct2.push(variable.Abbreviation.toLowerCase());
            }
        } else if (variable.Tissue === 'cortical' && needed) {
            if (reportRequirements.xct1.optional.scan.indexOf(key) > -1) {
                corticalVariables.xct1.push(variable.Abbreviation.toLowerCase());
            }
            if (reportRequirements.xct2.optional.scan.indexOf(key) > -1) {
                corticalVariables.xct2.push(variable.Abbreviation.toLowerCase());
            }
        } else if (variable.Tissue === 'both') {
            if (reportRequirements.xct1.optional.scan.indexOf(key) > -1) {
                bothVariables.xct1.push(variable.Abbreviation.toLowerCase());
            }
            if (reportRequirements.xct2.optional.scan.indexOf(key) > -1) {
                bothVariables.xct2.push(variable.Abbreviation.toLowerCase());
            }
        }

        if (!variable.Alternatives) {
            continue;
        }

        let alternatives = variable.Alternatives.toLowerCase().split(',');
        for (let alternative of alternatives) {
            alternativeMap[alternative.trim()] = { originalKey: key, variable: variable };
        }
    }

    isInitialized = true;
    return variables;
};

const initializeVariables = async () => {
    if (isInitialized && Object.keys(variables).length > 0) {
        return variables;
    }

    if (initializationPromise) {
        return initializationPromise;
    }

    initializationPromise = (async () => {
        try {
            const response = await fetch('/api/variables', {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch variables: ${response.status}`);
            }

            const apiVariables = await response.json();
            return buildDerivedStructures(apiVariables);
        } catch (error) {
            console.error('Falling back to bundled variables.json:', error);
            return buildDerivedStructures(fallbackVariables);
        } finally {
            initializationPromise = null;
        }
    })();

    return initializationPromise;
};

function findFromAlternative(alternativeName) {
    if (alternativeName.startsWith('r_') || alternativeName.startsWith('t_')) {
        const suffix = alternativeName.slice(0, 2);
        const noSuffix = alternativeName.slice(2);
        if (variables[noSuffix]) {
            return { originalKey: alternativeName, variable: variables[noSuffix] };
        } else if (alternativeMap[noSuffix.toLowerCase()]) {
            const copy = { ...alternativeMap[noSuffix.toLowerCase()] };
            copy.originalKey = suffix + copy.originalKey;
            return copy;
        }

    }

    if (variables[alternativeName]) {
        return { originalKey: alternativeName, variable: variables[alternativeName] };
    }
    let result = alternativeMap[alternativeName.toLowerCase()];
    if (result) {
        return result;
    }
    return null;
}

function getVariable(key) {
    let result = variables[key];
    if (!result) {
        result = findFromAlternative(key) ? findFromAlternative(key).variable : { Abbreviation: key.charAt(0).toUpperCase() + key.slice(1), Units: '' };
    }
    return result;
}

function parseVariable(variable) {
    if (variable.startsWith('r_') || variable.startsWith('t_')) {
        return getVariable(variable.slice(2)).Abbreviation;
    }
    return getVariable(variable).Abbreviation;
}

function isOptional(variable, reportType, group) {
    if (group === 'scan' && variable === 'measno')
        return false;
    if (reportRequirements[reportType].optional[group].indexOf(variable) > -1 && variable !== 'measno')
        return true;

    const alternative = findFromAlternative(variable);
    if (alternative) {
        return reportRequirements[reportType].optional[group].indexOf(variable) > -1;
    }
}

function isRequired(variable, reportType, group) {
    if (reportRequirements[reportType].required[group].includes(variable))
        return true;
    const alternative = findFromAlternative(variable);
    if (alternative)
        return reportRequirements[reportType].required[group].includes(alternative.originalKey);
}

// Export the variables object
export {
    variables,
    initializeVariables,
    trabecularVariables,
    corticalVariables,
    reportRequirements,
    bothVariables,
    getVariable,
    findFromAlternative,
    parseVariable,
    isOptional,
    isRequired
};
